import { type AxiosResponse, AxiosError } from 'axios';
import { z } from 'zod';
import { 
  type ApiResponse, 
  type ApiErrorResponse, 
  SuccessResponseSchema, 
  ErrorResponseSchema 
} from '../../types/api/common';

/**
 * Response handler for processing and validating API responses
 * Features:
 * - Response validation with Zod schemas
 * - Data transformation
 * - Error normalization
 * - Type safety enforcement
 * - Response logging and monitoring
 */
export class ResponseHandler {
  /**
   * Handle successful responses
   */
  public handleSuccess<T>(response: AxiosResponse): ApiResponse<T> {
    try {
      // Check if the response indicates an error (success: false) even with HTTP 200
      if (response.data && typeof response.data === 'object' && response.data.success === false) {
        console.error('API returned error with HTTP 200:', response.data);
        // Treat as an error response
        const errorResponse = this.adaptErrorStructure(response.data);
        throw errorResponse;
      }
      
      // Validate response structure
      const validatedResponse = this.validateSuccessResponse<T>(response.data);
      
      // Log successful response in development
      this.logSuccess(response, validatedResponse);
      
      return validatedResponse;
    } catch (error) {
      // If it's already an error response, re-throw it
      if (error && typeof error === 'object' && 'success' in error && error.success === false) {
        throw error;
      }
      // If validation fails, create a fallback response
      console.warn('Response validation failed, creating fallback response:', error);
      return this.createFallbackSuccessResponse<T>(response);
    }
  }

  /**
   * Handle error responses
   */
  public handleError(error: any): ApiErrorResponse {
    try {
      if (error.response) {
        // Server responded with error status
        return this.handleServerError(error as AxiosError);
      } else if (error.request) {
        // Request was made but no response received
        return this.handleNetworkError(error as AxiosError);
      } else {
        // Something else happened
        return this.handleGenericError(error);
      }
    } catch (handlingError) {
      console.error('Error in error handler:', handlingError);
      return this.createFallbackErrorResponse(error);
    }
  }

  /**
   * Validate successful response using Zod schema
   */
  private validateSuccessResponse<T>(data: any): ApiResponse<T> {
    // Create a generic success response schema (with optional requestId for backward compatibility)
    const GenericSuccessSchema = SuccessResponseSchema(z.any()).extend({
      requestId: z.string().uuid().optional(),
    });
    
    try {
      const validated = GenericSuccessSchema.parse(data) as ApiResponse<T>;
      // Ensure requestId exists (generate if missing)
      if (!validated.requestId) {
        validated.requestId = this.generateRequestId();
      }
      return validated;
    } catch (validationError) {
      // If the response doesn't match expected structure, try to adapt it
      return this.adaptResponseStructure<T>(data);
    }
  }

  /**
   * Adapt response structure to match expected format
   */
  private adaptResponseStructure<T>(data: any): ApiResponse<T> {
    // If data is already in correct format (has success and data keys)
    if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
      // Ensure requestId exists (generate if missing)
      const adapted: ApiResponse<T> = {
        ...data,
        requestId: data.requestId || this.generateRequestId(),
        timestamp: data.timestamp || new Date().toISOString(),
        data: data.data as T,
      };
      return adapted;
    }

    // If data is the actual payload, wrap it
    return {
      success: true,
      message: 'Request completed successfully',
      timestamp: new Date().toISOString(),
      requestId: this.generateRequestId(),
      data: data as T,
    };
  }

  /**
   * Create fallback success response when validation fails
   */
  private createFallbackSuccessResponse<T>(response: AxiosResponse): ApiResponse<T> {
    return {
      success: true,
      message: 'Request completed successfully',
      timestamp: new Date().toISOString(),
      requestId: response.config.headers?.['X-Request-ID'] as string || this.generateRequestId(),
      data: response.data as T,
    };
  }

  /**
   * Handle server errors (4xx, 5xx)
   */
  private handleServerError(error: AxiosError): ApiErrorResponse {
    const response = error.response!;
    const requestId = error.config?.headers?.['X-Request-ID'] as string || this.generateRequestId();

    try {
      // Try to validate error response structure
      const validatedError = this.validateErrorResponse(response.data);
      return validatedError;
    } catch (validationError) {
      // Create standardized error response
      return this.createStandardErrorResponse(error, requestId);
    }
  }

  /**
   * Validate error response using Zod schema
   */
  private validateErrorResponse(data: any): ApiErrorResponse {
    try {
      return ErrorResponseSchema.parse(data);
    } catch (validationError) {
      // If validation fails, adapt the structure
      return this.adaptErrorStructure(data);
    }
  }

  /**
   * Adapt error structure to match expected format
   */
  private adaptErrorStructure(data: any): ApiErrorResponse {
    // If data is already in correct format
    if (data && typeof data === 'object' && 'success' in data && 'error' in data) {
      return data as ApiErrorResponse;
    }

    // Extract error information from various possible structures
    let errorCode = 'UNKNOWN_ERROR';
    let errorType: ApiErrorResponse['error']['type'] = 'SERVER_ERROR';
    let message = 'An error occurred';
    let details = data;

    if (typeof data === 'string') {
      message = data;
    } else if (data && typeof data === 'object') {
      message = data.message || data.error || message;
      errorCode = data.code || data.errorCode || errorCode;
      details = data.details || data;
    }

    return {
      success: false,
      message,
      timestamp: new Date().toISOString(),
      requestId: this.generateRequestId(),
      error: {
        code: errorCode,
        type: errorType,
        details,
      },
    };
  }

  /**
   * Create standardized error response
   */
  private createStandardErrorResponse(error: AxiosError, requestId: string): ApiErrorResponse {
    const response = error.response!;
    const status = response.status;
    
    // Map HTTP status codes to error types
    const errorTypeMap: Record<number, ApiErrorResponse['error']['type']> = {
      400: 'VALIDATION',
      401: 'AUTHENTICATION',
      403: 'AUTHORIZATION',
      404: 'NOT_FOUND',
      422: 'VALIDATION',
      429: 'SERVER_ERROR',
      500: 'SERVER_ERROR',
      502: 'SERVER_ERROR',
      503: 'SERVER_ERROR',
      504: 'SERVER_ERROR',
    };

    const errorMessageMap: Record<number, string> = {
      400: 'Bad request. Please check your input.',
      401: 'Authentication required. Please log in.',
      403: 'You do not have permission to perform this action.',
      404: 'The requested resource was not found.',
      422: 'Validation failed. Please check your input.',
      429: 'Too many requests. Please try again later.',
      500: 'Internal server error. Please try again later.',
      502: 'Bad gateway. Please try again later.',
      503: 'Service unavailable. Please try again later.',
      504: 'Gateway timeout. Please try again later.',
    };

    const errorType = errorTypeMap[status] || 'SERVER_ERROR';
    const message = errorMessageMap[status] || 'An unexpected error occurred.';

    return {
      success: false,
      message,
      timestamp: new Date().toISOString(),
      requestId,
      error: {
        code: `HTTP_${status}`,
        type: errorType,
        details: response.data as Record<string, unknown>,
      },
    };
  }

  /**
   * Handle network errors
   */
  private handleNetworkError(error: AxiosError): ApiErrorResponse {
    const requestId = error.config?.headers?.['X-Request-ID'] as string || this.generateRequestId();

    let message = 'Network error occurred.';
    let code = 'NETWORK_ERROR';

    if (error.code === 'ECONNABORTED') {
      message = 'Request timeout. Please try again.';
      code = 'TIMEOUT';
    } else if (error.message.includes('Network Error')) {
      message = 'Network connection failed. Please check your internet connection.';
      code = 'CONNECTION_ERROR';
    }

    return {
      success: false,
      message,
      timestamp: new Date().toISOString(),
      requestId,
      error: {
        code,
        type: 'NETWORK_ERROR',
        details: {
          message: error.message,
          code: error.code,
        },
      },
    };
  }

  /**
   * Handle generic errors
   */
  private handleGenericError(error: any): ApiErrorResponse {
    const requestId = this.generateRequestId();

    return {
      success: false,
      message: 'An unexpected error occurred.',
      timestamp: new Date().toISOString(),
      requestId,
      error: {
        code: 'GENERIC_ERROR',
        type: 'SERVER_ERROR',
        details: {
          message: error?.message || 'Unknown error',
          stack: error?.stack,
        },
      },
    };
  }

  /**
   * Create fallback error response when error handling fails
   */
  private createFallbackErrorResponse(error: any): ApiErrorResponse {
    return {
      success: false,
      message: 'A critical error occurred while processing the response.',
      timestamp: new Date().toISOString(),
      requestId: this.generateRequestId(),
      error: {
        code: 'HANDLER_ERROR',
        type: 'SERVER_ERROR',
        details: {
          originalError: error?.message || 'Unknown error',
        },
      },
    };
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Log successful responses in development
   */
  private logSuccess<T>(response: AxiosResponse, validatedResponse: ApiResponse<T>): void {
    if (import.meta.env.MODE === 'development') {
      console.group(`✅ Response Handler - Success`);
      console.log('URL:', response.config.url);
      console.log('Status:', response.status);
      console.log('Request ID:', validatedResponse.requestId);
      console.log('Data:', validatedResponse.data);
      console.groupEnd();
    }
  }

  /**
   * Transform response data using custom transformer
   */
  public transformResponse<T, R>(
    response: ApiResponse<T>, 
    transformer: (data: T) => R
  ): ApiResponse<R> {
    try {
      const transformedData = transformer(response.data);
      
      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error('Response transformation failed:', error);
      throw new Error('Failed to transform response data');
    }
  }

  /**
   * Validate response data against a Zod schema
   */
  public validateResponseData<T>(
    response: ApiResponse<unknown>, 
    schema: z.ZodSchema<T>
  ): ApiResponse<T> {
    try {
      const validatedData = schema.parse(response.data);
      
      return {
        ...response,
        data: validatedData,
      };
    } catch (error) {
      console.error('Response data validation failed:', error);
      throw new Error('Response data validation failed');
    }
  }

  /**
   * Extract specific fields from response data
   */
  public extractFields<T extends Record<string, any>, K extends keyof T>(
    response: ApiResponse<T>,
    fields: K[]
  ): ApiResponse<Pick<T, K>> {
    try {
      const extractedData = fields.reduce((acc, field) => {
        if (field in response.data) {
          acc[field] = response.data[field];
        }
        return acc;
      }, {} as Pick<T, K>);

      return {
        ...response,
        data: extractedData,
      };
    } catch (error) {
      console.error('Field extraction failed:', error);
      throw new Error('Failed to extract fields from response');
    }
  }

  /**
   * Merge multiple responses into a single response
   */
  public mergeResponses<T extends Record<string, any>>(
    responses: ApiResponse<Partial<T>>[]
  ): ApiResponse<T> {
    try {
      const mergedData = responses.reduce((acc, response) => {
        return { ...acc, ...response.data };
      }, {} as T);

      const latestResponse = responses[responses.length - 1];

      return {
        ...latestResponse,
        data: mergedData,
      };
    } catch (error) {
      console.error('Response merge failed:', error);
      throw new Error('Failed to merge responses');
    }
  }
}
